import React, {useCallback} from 'react';

const LAYOUTS = {
  hebrew: {
    label: 'Hebrew keyboard',
    fontClass: 'font-hebrew',
    rows: [
      ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט', 'י',
        'כ', 'ל', 'מ', 'נ', 'ס'],
      ['ע', 'פ', 'צ', 'ק', 'ר', 'ש', 'ת', 'ך', 'ם', 'ן', 'ף', 'ץ'],
    ],
  },
  farsi: {
    label: 'Persian keyboard',
    fontClass: 'font-farsi',
    rows: [
      ['ا', 'ب', 'پ', 'ت', 'ث', 'ج', 'چ', 'ح', 'خ', 'د',
        'ذ', 'ر', 'ز', 'ژ', 'س', 'ش'],
      ['ص', 'ض', 'ط', 'ظ', 'ع', 'غ', 'ف', 'ق', 'ک', 'گ',
        'ل', 'م', 'ن', 'و', 'ه', 'ی'],
    ],
  },
};

function getSelection(input) {
  const start = input.selectionStart;
  const end = input.selectionEnd;
  if (start == null || end == null) {
    return {start: input.value.length, end: input.value.length};
  }
  return {start, end};
}

// Write a value into a (possibly controlled) input and notify React by
// dispatching a native "input" event. Works for both controlled
// (onChange-driven) and uncontrolled inputs.
function applyInputValue(input, nextValue, caret) {
  const proto = window.HTMLInputElement.prototype;
  const descriptor = Object.getOwnPropertyDescriptor(proto, 'value');
  if (descriptor && typeof descriptor.set === 'function') {
    descriptor.set.call(input, nextValue);
  } else {
    input.value = nextValue;
  }
  input.dispatchEvent(new Event('input', {bubbles: true}));
  if (caret != null) {
    input.selectionStart = input.selectionEnd = caret;
  }
}

function insertText(input, text) {
  if (!input) return;
  const {start, end} = getSelection(input);
  const next = input.value.slice(0, start) + text + input.value.slice(end);
  applyInputValue(input, next, start + text.length);
  input.focus();
}

function backspace(input) {
  if (!input) return;
  const {start, end} = getSelection(input);
  let next;
  let caret;
  if (start !== end) {
    next = input.value.slice(0, start) + input.value.slice(end);
    caret = start;
  } else if (start > 0) {
    next = input.value.slice(0, start - 1) + input.value.slice(end);
    caret = start - 1;
  } else {
    return;
  }
  applyInputValue(input, next, caret);
  input.focus();
}

export const MiniKeyboard = ({language, inputRef, onClose}) => {
  const layout = LAYOUTS[language];

  const handleKey = useCallback((ch) => {
    if (inputRef && inputRef.current) {
      insertText(inputRef.current, ch);
    }
  }, [inputRef]);

  const handleBackspace = useCallback(() => {
    if (inputRef && inputRef.current) {
      backspace(inputRef.current);
    }
  }, [inputRef]);

  const handleSpace = useCallback(() => {
    if (inputRef && inputRef.current) {
      insertText(inputRef.current, ' ');
    }
  }, [inputRef]);

  // Keep the target input focused so its caret (insertion point) is
  // preserved across the click; otherwise the button steals focus.
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
  }, []);

  if (!layout) return null;

  return (
    <div
      className={`mini-keyboard ${layout.fontClass}`}
      dir="rtl"
      role="group"
      aria-label={layout.label}
    >
      <div className="mini-keyboard-header">
        <span className="mini-keyboard-title">{layout.label}</span>
        {onClose && (
          <button
            type="button"
            className="mini-keyboard-close"
            onClick={onClose}
            onMouseDown={handleMouseDown}
            aria-label="Hide keyboard"
          >
            ✕
          </button>
        )}
      </div>
      <div className="mini-keyboard-body">
        {layout.rows.map((row, rowIdx) => (
          <div className="mini-keyboard-row" key={rowIdx}>
            {row.map((ch) => (
              <button
                type="button"
                className="mini-keyboard-key"
                key={ch}
                onClick={() => handleKey(ch)}
                onMouseDown={handleMouseDown}
              >
                {ch}
              </button>
            ))}
          </div>
        ))}
        <div className="mini-keyboard-row mini-keyboard-controls">
          <button
            type="button"
            className="mini-keyboard-key mini-keyboard-key-wide"
            onClick={handleSpace}
            onMouseDown={handleMouseDown}
            aria-label="Space"
          >
            ␣
          </button>
          <button
            type="button"
            className="mini-keyboard-key"
            onClick={handleBackspace}
            onMouseDown={handleMouseDown}
            aria-label="Backspace"
          >
            ⌫
          </button>
        </div>
      </div>
    </div>
  );
};

export default MiniKeyboard;
